import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, ShieldBan, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { type ColumnDef, DataTable, DataTableColumnHeader } from "@/components/ui/data-table";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NoticeBanner } from "@/components/ui/notice-banner";
import { useDebounce } from "@/lib/use-debounce";
import {
	type Article,
	type BlockRule,
	type BlockRuleAction,
	type BlockRuleKind,
	formatDateTime,
	HELPDESK_QUERY_KEY,
	helpdeskApi,
	type Ticket,
	useHelpdeskKey,
	useHelpdeskMutation,
} from "./api";
import { useTableExport } from "./use-table-export";

export const RULE_KIND_LABEL: Record<BlockRuleKind, string> = {
	address: "Adres",
	domain: "Domena",
	phrase: "Fraza",
	subject: "Fraza w temacie",
};

export const RULE_ACTION_LABEL: Record<BlockRuleAction, string> = {
	quarantine: "Kwarantanna",
	history: "Tylko w historii apteki",
};

const CHOICES: { value: "none" | BlockRuleKind; label: string }[] = [
	{ value: "none", label: "Tylko to zgłoszenie" },
	{ value: "address", label: "Blokuj adres nadawcy" },
	{ value: "domain", label: "Blokuj całą domenę" },
	{ value: "phrase", label: "Blokuj frazę w temacie lub treści" },
];

/**
 * „Zgłoś spam”: zgłoszenie do kwarantanny i opcjonalna reguła na przyszłe maile.
 * Przed zapisem podgląd: ile istniejących wiadomości reguła by złapała i czy adres
 * albo domena należy do sparowanej apteki.
 */
export function ReportSpamDialog({
	ticket,
	article,
	open,
	onOpenChange,
	onDone,
}: {
	ticket: Pick<Ticket, "number" | "subject" | "contactEmail">;
	article?: Pick<Article, "fromAddr">;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onDone?: () => void;
}) {
	const key = useHelpdeskKey();
	const from = (article?.fromAddr ?? ticket.contactEmail ?? "").toLowerCase();
	const [choice, setChoice] = useState<"none" | BlockRuleKind>("none");
	const [values, setValues] = useState<Record<BlockRuleKind, string>>({
		address: "",
		domain: "",
		phrase: "",
		subject: "",
	});
	const [reason, setReason] = useState("");
	// Każde otwarcie od nowa: propozycje z nadawcy i tematu tego zgłoszenia.
	useEffect(() => {
		if (!open) return;
		setChoice(from ? "address" : "none");
		setValues({
			address: from,
			domain: from.split("@")[1] ?? "",
			phrase: ticket.subject,
			subject: ticket.subject,
		});
		setReason("");
	}, [open, from, ticket.subject]);

	const value = choice === "none" ? "" : values[choice].trim();
	const debounced = useDebounce(value, 400);
	const preview = useQuery({
		queryKey: key("block-rule-preview", choice, debounced),
		queryFn: () => helpdeskApi.blockRulePreview(choice as BlockRuleKind, debounced),
		enabled: open && choice !== "none" && debounced.length > 0,
		retry: false,
		staleTime: 30_000,
	});
	const report = useHelpdeskMutation(
		(rule?: { kind: BlockRuleKind; value: string; reason: string }) =>
			helpdeskApi.reportSpam(ticket.number, rule),
		{ success: "Przeniesiono do spamu", error: "Nie udało się oznaczyć jako spam" },
	);
	const paired = preview.data?.paired ?? [];

	return (
		<ConfirmDialog
			open={open}
			onOpenChange={onOpenChange}
			title={`Zgłosić #${ticket.number} jako spam?`}
			description="Zgłoszenie zniknie ze skrzynki i trafi do kwarantanny (widok Spam): bez auto-odpowiedzi, AI i diagnozy."
			confirmLabel="Przenieś do spamu"
			variant="warning"
			isLoading={report.isPending}
			// Reguła dopiero z podglądem dla bieżącej wartości — razem z ostrzeżeniem o sparowanej aptece.
			confirmDisabled={choice !== "none" && (debounced !== value || !preview.isSuccess)}
			onConfirm={() =>
				report.mutate(
					choice === "none" ? undefined : { kind: choice, value, reason: reason.trim() },
					{
						onSuccess: () => {
							onOpenChange(false);
							onDone?.();
						},
					},
				)
			}
		>
			<div className="space-y-2 text-sm">
				<fieldset className="space-y-1" aria-label="Blokada przyszłych maili">
					{CHOICES.map((c) => (
						<label key={c.value} className="flex items-center gap-2">
							<input
								type="radio"
								name="spam-rule"
								value={c.value}
								checked={choice === c.value}
								disabled={c.value !== "none" && c.value !== "phrase" && !from}
								onChange={() => setChoice(c.value)}
							/>
							{c.label}
						</label>
					))}
				</fieldset>
				{choice !== "none" ? (
					<div className="space-y-2">
						<Input
							aria-label="Wartość reguły"
							value={values[choice]}
							onChange={(e) => setValues((v) => ({ ...v, [choice]: e.target.value }))}
							className="h-8 text-xs"
						/>
						<Input
							aria-label="Powód blokady"
							placeholder="Powód (widoczny w panelu reguł)"
							value={reason}
							onChange={(e) => setReason(e.target.value)}
							className="h-8 text-xs"
						/>
						<p
							className="flex items-center gap-1.5 text-xs text-muted-foreground"
							aria-live="polite"
						>
							{preview.isFetching ? <Loader2 className="size-3 animate-spin" /> : null}
							{preview.isError
								? preview.error instanceof Error
									? preview.error.message
									: "Nieprawidłowa reguła"
								: preview.data
									? `Istniejące wiadomości, które złapie reguła: ${preview.data.matches}`
									: null}
						</p>
						{paired.length ? (
							<NoticeBanner
								tone="warning"
								title="Ten adres należy do sparowanej apteki"
								description={`${paired
									.slice(0, 3)
									.map((p) => `${p.name ?? `apteka ${p.idfirm}`} (${p.email})`)
									.join(
										", ",
									)}${paired.length > 3 ? ` i ${paired.length - 3} więcej` : ""} — jej maile też trafią do spamu.`}
							/>
						) : null}
					</div>
				) : null}
			</div>
		</ConfirmDialog>
	);
}

/**
 * Zgłoszenie w kwarantannie: powód i „To nie spam”. Baner znika razem ze spamem,
 * więc propozycję usunięcia reguły, która złapała mail, niesie toast z akcją.
 */
export function SpamBanner({ ticket }: { ticket: Pick<Ticket, "number" | "spamReason"> }) {
	const queryClient = useQueryClient();
	const restore = useHelpdeskMutation(() => helpdeskApi.notSpam(ticket.number), {
		error: "Nie udało się przywrócić zgłoszenia",
		onSuccess: ({ rule }) => {
			if (!rule) {
				toast.success("Przywrócono do skrzynki");
				return;
			}
			toast.warning(`Przywrócono. Usunąć regułę ${RULE_KIND_LABEL[rule.kind]}: ${rule.value}?`, {
				description: `Trafień: ${rule.hitCount}. Bez niej kolejne takie maile trafią do skrzynki.`,
				duration: 30_000,
				action: {
					label: "Usuń regułę",
					onClick: () =>
						helpdeskApi.deleteBlockRule(rule.id).then(
							() => {
								toast.success("Reguła usunięta");
								void queryClient.invalidateQueries({ queryKey: [...HELPDESK_QUERY_KEY] });
							},
							(e) =>
								toast.error("Nie udało się usunąć reguły", {
									description: e instanceof Error ? e.message : undefined,
								}),
						),
				},
			});
		},
	});
	return (
		<NoticeBanner
			tone="warning"
			icon={ShieldBan}
			title="Spam — kwarantanna"
			description={ticket.spamReason ?? undefined}
			action={
				<Button
					size="sm"
					className="h-7 text-xs"
					disabled={restore.isPending}
					onClick={() => restore.mutate(undefined)}
				>
					To nie spam
				</Button>
			}
		/>
	);
}

const RULE_COLUMN_LABELS = {
	kind: "Rodzaj",
	value: "Wartość",
	reason: "Powód",
	action: "Akcja",
	createdByName: "Dodał",
	createdAt: "Dodano",
	hitCount: "Trafienia",
	lastHitAt: "Ostatnie trafienie",
	enabled: "Włączona",
};

const selectClass = "h-8 w-full rounded-md border border-input bg-background px-2 text-xs";

/** Reguła dodana w panelu — zwykle „tylko w historii apteki” dla kolejnego alertu automatycznego. */
function AddRuleDialog({
	open,
	onOpenChange,
}: {
	open: boolean;
	onOpenChange: (o: boolean) => void;
}) {
	const key = useHelpdeskKey();
	const [kind, setKind] = useState<BlockRuleKind>("subject");
	const [action, setAction] = useState<BlockRuleAction>("history");
	const [value, setValue] = useState("");
	const [reason, setReason] = useState("");
	useEffect(() => {
		if (!open) return;
		setKind("subject");
		setAction("history");
		setValue("");
		setReason("");
	}, [open]);
	const trimmed = value.trim();
	const debounced = useDebounce(trimmed, 400);
	const preview = useQuery({
		queryKey: key("block-rule-preview", kind, debounced),
		queryFn: () => helpdeskApi.blockRulePreview(kind, debounced),
		enabled: open && debounced.length > 0,
		retry: false,
		staleTime: 30_000,
	});
	const create = useHelpdeskMutation(
		() => helpdeskApi.createBlockRule({ kind, value: trimmed, reason: reason.trim(), action }),
		{
			success: "Reguła dodana",
			error: "Nie udało się dodać reguły",
			onSuccess: () => onOpenChange(false),
		},
	);
	return (
		<ConfirmDialog
			open={open}
			onOpenChange={onOpenChange}
			title="Dodać regułę?"
			description="Działa na nowe maile. Dotychczasowe zgłoszenia przenosi „Zastosuj do istniejących” w tabeli."
			confirmLabel="Dodaj regułę"
			variant="default"
			isLoading={create.isPending}
			confirmDisabled={debounced !== trimmed || !preview.isSuccess}
			onConfirm={() => create.mutate(undefined)}
		>
			<div className="space-y-2 text-sm">
				<select
					aria-label="Rodzaj reguły"
					value={kind}
					onChange={(e) => setKind(e.target.value as BlockRuleKind)}
					className={selectClass}
				>
					{Object.entries(RULE_KIND_LABEL).map(([v, label]) => (
						<option key={v} value={v}>
							{label}
						</option>
					))}
				</select>
				<Input
					aria-label="Wartość reguły"
					placeholder={kind === "subject" ? "np. Nieskończone odświeżanie" : undefined}
					value={value}
					onChange={(e) => setValue(e.target.value)}
					className="h-8 text-xs"
				/>
				<Input
					aria-label="Powód reguły"
					placeholder="Powód (widoczny w panelu reguł)"
					value={reason}
					onChange={(e) => setReason(e.target.value)}
					className="h-8 text-xs"
				/>
				<select
					aria-label="Akcja reguły"
					value={action}
					onChange={(e) => setAction(e.target.value as BlockRuleAction)}
					className={selectClass}
				>
					{Object.entries(RULE_ACTION_LABEL).map(([v, label]) => (
						<option key={v} value={v}>
							{label}
						</option>
					))}
				</select>
				<p className="flex items-center gap-1.5 text-xs text-muted-foreground" aria-live="polite">
					{preview.isFetching ? <Loader2 className="size-3 animate-spin" /> : null}
					{preview.isError
						? preview.error instanceof Error
							? preview.error.message
							: "Nieprawidłowa reguła"
						: preview.data
							? kind === "subject"
								? `Istniejące zgłoszenia z tym w temacie: ${preview.data.matches}`
								: `Istniejące wiadomości, które złapie reguła: ${preview.data.matches}`
							: null}
				</p>
			</div>
		</ConfirmDialog>
	);
}

/**
 * Panel reguł (konfiguracja): kto i kiedy dodał, po co, ile trafień i co robi z mailem —
 * kwarantanna albo „tylko w historii apteki”; wyłącz, usuń albo zastosuj do istniejących.
 */
export function BlockRulesPanel() {
	const key = useHelpdeskKey();
	const { data = [], isLoading } = useQuery({
		queryKey: key("block-rules"),
		queryFn: helpdeskApi.blockRules,
		staleTime: 30_000,
	});
	const update = useHelpdeskMutation(
		({ id, ...patch }: { id: number; enabled?: boolean; action?: BlockRuleAction }) =>
			helpdeskApi.setBlockRule(id, patch),
		{ error: "Nie udało się zapisać reguły" },
	);
	const [removing, setRemoving] = useState<BlockRule | null>(null);
	const remove = useHelpdeskMutation((id: number) => helpdeskApi.deleteBlockRule(id), {
		success: "Reguła usunięta",
		error: "Nie udało się usunąć reguły",
	});
	const [applying, setApplying] = useState<BlockRule | null>(null);
	const apply = useHelpdeskMutation((id: number) => helpdeskApi.applyBlockRule(id), {
		success: ({ applied }) => `Przeniesiono do historii aptek: ${applied}`,
		error: "Nie udało się zastosować reguły",
		onSuccess: () => setApplying(null),
	});
	const [adding, setAdding] = useState(false);
	const tableExport = useTableExport();

	const columns = useMemo<ColumnDef<BlockRule>[]>(
		() => [
			{
				accessorKey: "kind",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Rodzaj" />,
				cell: ({ row }) => RULE_KIND_LABEL[row.original.kind],
				size: 90,
				meta: {
					facetedOptions: Object.entries(RULE_KIND_LABEL).map(([value, label]) => ({
						value,
						label,
					})),
				},
			},
			{
				accessorKey: "value",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Wartość" />,
				cell: ({ row }) => <span className="truncate font-mono">{row.original.value}</span>,
				size: 220,
				meta: { mobileRole: "title" },
			},
			{
				accessorKey: "reason",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Powód" />,
				cell: ({ row }) => <span className="truncate">{row.original.reason || "—"}</span>,
				size: 220,
				meta: { fill: true },
			},
			{
				accessorKey: "action",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Akcja" />,
				cell: ({ row }) => (
					<span className="flex items-center gap-1.5">
						<select
							aria-label={`Akcja reguły ${row.original.value}`}
							value={row.original.action}
							onChange={(e) =>
								update.mutate({ id: row.original.id, action: e.target.value as BlockRuleAction })
							}
							className="h-6 rounded-md border border-input bg-background px-1 text-xs"
						>
							{Object.entries(RULE_ACTION_LABEL).map(([v, label]) => (
								<option key={v} value={v}>
									{label}
								</option>
							))}
						</select>
						{row.original.action === "history" ? (
							<Button
								size="sm"
								variant="outline"
								className="h-6 px-2 text-xs"
								onClick={() => setApplying(row.original)}
							>
								Zastosuj do istniejących
							</Button>
						) : null}
					</span>
				),
				size: 300,
				meta: {
					facetedOptions: Object.entries(RULE_ACTION_LABEL).map(([value, label]) => ({
						value,
						label,
					})),
				},
			},
			{
				accessorKey: "createdByName",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Dodał" />,
				cell: ({ row }) => row.original.createdByName ?? "—",
				size: 150,
				meta: { mobileRole: "subtitle" },
			},
			{
				accessorKey: "createdAt",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Dodano" />,
				cell: ({ row }) => (
					<span className="tabular-nums">{formatDateTime(row.original.createdAt)}</span>
				),
				size: 130,
				meta: { isDate: true },
			},
			{
				accessorKey: "hitCount",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Trafienia" isNumeric />
				),
				cell: ({ row }) => <span className="tabular-nums">{row.original.hitCount}</span>,
				size: 90,
				meta: { isNumeric: true, mobileRole: "measure" },
			},
			{
				accessorKey: "lastHitAt",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Ostatnie trafienie" />
				),
				cell: ({ row }) => (
					<span className="tabular-nums">
						{row.original.lastHitAt ? formatDateTime(row.original.lastHitAt) : "—"}
					</span>
				),
				size: 140,
				meta: { isDate: true },
			},
			{
				id: "enabled",
				accessorFn: (r) => (r.enabled ? "tak" : "nie"),
				header: ({ column }) => <DataTableColumnHeader column={column} title="Włączona" />,
				cell: ({ row }) => (
					<input
						type="checkbox"
						aria-label={`Reguła ${row.original.value} włączona`}
						checked={row.original.enabled}
						onChange={(e) => update.mutate({ id: row.original.id, enabled: e.target.checked })}
					/>
				),
				size: 90,
				meta: {
					facetedOptions: [
						{ value: "tak", label: "tak" },
						{ value: "nie", label: "nie" },
					],
				},
			},
			{
				id: "remove",
				header: "",
				cell: ({ row }) => (
					<Button
						size="icon"
						variant="ghost"
						className="size-6"
						aria-label={`Usuń regułę ${row.original.value}`}
						onClick={() => setRemoving(row.original)}
					>
						<Trash2 className="size-3" />
					</Button>
				),
				size: 40,
				enableSorting: false,
				enableColumnFilter: false,
				meta: { alwaysLast: true, mobileRole: "actions" },
			},
		],
		[update.mutate],
	);

	return (
		<section className="flex flex-col gap-2">
			<div className="flex items-center gap-2">
				<h2 className="text-sm font-semibold">Reguły</h2>
				<span className="truncate text-xs text-muted-foreground">
					Kwarantanna: mail idzie do spamu, nie jest kasowany. Tylko w historii apteki: alert „!!!”
					zamknięty przy aptece z numeru w temacie, poza skrzynką; zwykły mail apteki zostaje.
				</span>
				{isLoading ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}
				<Button size="sm" className="ml-auto h-8 text-xs" onClick={() => setAdding(true)}>
					Dodaj regułę
				</Button>
				<Button
					size="sm"
					variant="outline"
					className="h-8 text-xs"
					disabled={!tableExport.ready || tableExport.exporting}
					onClick={tableExport.run}
				>
					Eksportuj
				</Button>
			</div>
			<div className="flex min-h-[16rem] flex-col rounded-md border border-border bg-card">
				<DataTable
					tableId="helpdesk.block-rules"
					className="min-h-0 w-full flex-1"
					columns={columns}
					data={data}
					getRowId={(r) => String(r.id)}
					enableToolbar
					enableColumnVisibility
					onExportReady={tableExport.onExportReady}
					exportFileName="Helpdesk_reguly_blokad"
					columnLabels={RULE_COLUMN_LABELS}
				/>
			</div>
			<ConfirmDialog
				open={!!removing}
				onOpenChange={(o) => !o && setRemoving(null)}
				title="Usunąć regułę?"
				description={
					removing
						? `${RULE_KIND_LABEL[removing.kind]}: ${removing.value}. Maile już w kwarantannie albo w historii aptek tam zostają.`
						: undefined
				}
				confirmLabel="Usuń"
				variant="destructive"
				isLoading={remove.isPending}
				onConfirm={() =>
					removing && remove.mutate(removing.id, { onSuccess: () => setRemoving(null) })
				}
			/>
			<ConfirmDialog
				open={!!applying}
				onOpenChange={(o) => !o && setApplying(null)}
				title="Przenieść pasujące zgłoszenia do historii aptek?"
				description={
					applying
						? `${RULE_KIND_LABEL[applying.kind]}: ${applying.value}. Zgłoszenia z apteką (także z OTRS) zostaną zamknięte i znikną ze skrzynki; w czacie apteki zostaną jako szare linie. Apteki nie dostaną maili.`
						: undefined
				}
				confirmLabel="Przenieś"
				variant="warning"
				isLoading={apply.isPending}
				onConfirm={() => applying && apply.mutate(applying.id)}
			/>
			<AddRuleDialog open={adding} onOpenChange={setAdding} />
		</section>
	);
}
